import logoDeckoSvg from '../svg/logo-decko.svg?raw'
import iconBackSvg from '../svg/icon-back.svg?raw'
import iconBookSvg from '../svg/icon-book.svg?raw'
import iconAnimationSvg from '../svg/icon-animation.svg?raw'
import iconOptionsSvg from '../svg/icon-options.svg?raw'
import iconFullscreenOnSvg from '../svg/icon-fullscreen-on.svg?raw'
import iconFullscreenOffSvg from '../svg/icon-fullscreen-off.svg?raw'

export class GameNavigation {
  private navElement: HTMLElement
  private logoLink: HTMLAnchorElement | null = null
  private backButton: HTMLButtonElement | null = null
  private chapterButton: HTMLButtonElement | null = null
  private debugButton: HTMLButtonElement
  private settingsButton: HTMLButtonElement
  private fullscreenButton: HTMLButtonElement
  private onBackCallback?: () => void
  private onChapterCallback?: () => void
  private onDebugCallback?: () => void
  private onSettingsCallback?: () => void
  private isFullscreen = false

  private escapeToNavHandler = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return
    if (!this.backButton || this.navElement.classList.contains('hidden')) return
    if (this.backButton.classList.contains('hidden')) return
    if (document.querySelector('.modal:not(.modal--hidden)')) return
    if (document.querySelector('.homework-overlay[aria-hidden="false"]')) return
    this.backButton.focus()
  }

  constructor(private container: HTMLElement) {
    this.navElement = container.querySelector('.game-navigation') as HTMLElement
    if (!this.navElement) throw new Error('Game navigation element not found in HTML')

    this.logoLink = this.navElement.querySelector('.game-navigation__logo') as HTMLAnchorElement | null
    if (this.logoLink) this.logoLink.innerHTML = logoDeckoSvg

    this.backButton = this.navElement.querySelector('.game-navigation__back-button') as HTMLButtonElement | null
    this.chapterButton = this.navElement.querySelector('.game-navigation__button--chapter') as HTMLButtonElement | null
    this.debugButton = this.navElement.querySelector('.game-navigation__button--debug') as HTMLButtonElement
    this.settingsButton = this.navElement.querySelector('.game-navigation__button--settings') as HTMLButtonElement
    this.fullscreenButton = this.navElement.querySelector('.game-navigation__button--fullscreen') as HTMLButtonElement
    if (!this.debugButton || !this.settingsButton || !this.fullscreenButton) {
      throw new Error('Required navigation buttons not found in HTML')
    }

    if (this.backButton) this.backButton.innerHTML = iconBackSvg
    if (this.chapterButton) this.chapterButton.innerHTML = iconBookSvg
    this.debugButton.innerHTML = '😇'
    this.settingsButton.innerHTML = iconOptionsSvg
    this.fullscreenButton.innerHTML = this.isFullscreen ? iconFullscreenOffSvg : iconFullscreenOnSvg

    this.attachButtonListeners()
    document.addEventListener('keydown', this.escapeToNavHandler)

    if (isFullscreenSupported()) {
      this.updateFullscreenButton()
      this.fullscreenButton.addEventListener('click', () => this.toggleFullscreen())
      this.attachFullscreenChangeListeners()
    } else {
      this.fullscreenButton.style.display = 'none'
    }
  }

  private attachButtonListeners(): void {
    this.backButton?.addEventListener('click', () => this.onBackCallback?.())
    this.chapterButton?.addEventListener('click', () => this.onChapterCallback?.())
    this.debugButton.addEventListener('click', () => this.onDebugCallback?.())
    this.settingsButton.addEventListener('click', () => this.onSettingsCallback?.())
  }

  private attachFullscreenChangeListeners(): void {
    const update = () => {
      this.isFullscreen = !!getFullscreenElement()
      this.updateFullscreenButton()
    }
    document.addEventListener('fullscreenchange', update)
    document.addEventListener('webkitfullscreenchange', update)
    document.addEventListener('mozfullscreenchange', update)
    document.addEventListener('MSFullscreenChange', update)
  }

  private updateFullscreenButton(): void {
    this.fullscreenButton.innerHTML = this.isFullscreen ? iconFullscreenOffSvg : iconFullscreenOnSvg
    this.fullscreenButton.setAttribute('aria-label', this.isFullscreen ? 'Zavřít celou obrazovku' : 'Celá obrazovka')
  }

  private toggleFullscreen(): void {
    if (getFullscreenElement()) {
      exitFullscreen()
    } else {
      enterFullscreen(document.documentElement)
    }
  }

  show(showBackButton: boolean = true, showDebugButton: boolean = true): void {
    this.navElement.classList.remove('hidden')
    // Logo only on home screen (when back is hidden); back only on game screen
    if (this.logoLink) {
      if (showBackButton) {
        this.logoLink.classList.add('hidden')
      } else {
        this.logoLink.classList.remove('hidden')
      }
    }
    if (this.backButton) {
      if (showBackButton) {
        this.backButton.classList.remove('hidden')
      } else {
        this.backButton.classList.add('hidden')
      }
    }
    // Chapters button only on game screen (when back is shown)
    if (this.chapterButton) {
      if (showBackButton) {
        this.chapterButton.classList.remove('hidden')
      } else {
        this.chapterButton.classList.add('hidden')
      }
    }
    // Show/hide debug button based on parameter (only show on game screen)
    if (this.debugButton) {
      if (showDebugButton) {
        this.debugButton.classList.remove('hidden')
      } else {
        this.debugButton.classList.add('hidden')
      }
    }
  }

  hide(): void {
    this.navElement.classList.add('hidden')
  }

  setOnBack(callback: () => void): void {
    this.onBackCallback = callback
  }

  setOnDebug(callback: () => void): void {
    this.onDebugCallback = callback
  }

  setOnSettings(callback: () => void): void {
    this.onSettingsCallback = callback
  }

  setOnChapter(callback: () => void): void {
    this.onChapterCallback = callback
  }

  destroy(): void {
    document.removeEventListener('keydown', this.escapeToNavHandler)
    this.hide()
  }
}

function isFullscreenSupported(): boolean {
  const el = document.documentElement as HTMLElement & {
    requestFullscreen?: unknown
    webkitRequestFullscreen?: unknown
    mozRequestFullScreen?: unknown
    msRequestFullscreen?: unknown
  }
  return !!(el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen)
}

function getFullscreenElement(): Element | null {
  return (
    document.fullscreenElement ??
    (document as unknown as { webkitFullscreenElement?: Element }).webkitFullscreenElement ??
    (document as unknown as { mozFullScreenElement?: Element }).mozFullScreenElement ??
    (document as unknown as { msFullscreenElement?: Element }).msFullscreenElement ??
    null
  )
}

function enterFullscreen(element: HTMLElement): void {
  const el = element as HTMLElement & {
    requestFullscreen?: () => Promise<void>
    webkitRequestFullscreen?: () => void
    mozRequestFullScreen?: () => void
    msRequestFullscreen?: () => void
  }
  if (el.requestFullscreen) el.requestFullscreen()
  else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen()
  else if (el.mozRequestFullScreen) el.mozRequestFullScreen()
  else if (el.msRequestFullscreen) el.msRequestFullscreen()
}

function exitFullscreen(): void {
  const doc = document as Document & {
    exitFullscreen?: () => Promise<void>
    webkitExitFullscreen?: () => void
    mozCancelFullScreen?: () => void
    msExitFullscreen?: () => void
  }
  if (doc.exitFullscreen) doc.exitFullscreen()
  else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen()
  else if (doc.mozCancelFullScreen) doc.mozCancelFullScreen()
  else if (doc.msExitFullscreen) doc.msExitFullscreen()
}
